import { useState, useRef, useEffect, useCallback } from 'react'
import { useIntersection } from './useIntersection'
import {
    DataFetcher,
    InfiniteScrollOptions,
    InfiniteScrollState,
    ResponseAdapter,
} from './types'


/**
 * Хук-обёртка для ленивой бесконечной загрузки
 */
export function useInfiniteScroll<T, R>(
    fetchData: DataFetcher<T, R>,
    {
        pageSize = 10,
        threshold = 0.1,
        initialPage = 1,
        autoLoad = true,
        resetDeps = [],
        responseAdapter,
    }: InfiniteScrollOptions<T, R>,
) {
    const [state, setState] = useState<InfiniteScrollState<T>>({
        items: [],
        page: initialPage,
        hasMore: true,
        total: undefined,
        loading: false,
        error: null,
    })

    const stateRef = useRef(state)
    const fetchRef = useRef(fetchData)
    const adapterRef = useRef<ResponseAdapter<T, R>>(responseAdapter)
    const abortRef = useRef<AbortController>()
    const initialLoadRef = useRef(false)

    useEffect(() => { stateRef.current = state }, [state])
    useEffect(() => { fetchRef.current = fetchData }, [fetchData])
    useEffect(() => { adapterRef.current = responseAdapter }, [responseAdapter])

    const loadMore = useCallback(async () => {
        const { loading, hasMore, page } = stateRef.current
        if (loading || !hasMore) return

        abortRef.current?.abort()
        const controller = new AbortController()
        abortRef.current = controller

        // Запрос-фетчер (doLoad) реально не поддерживает отмену через signal, поэтому
        // .abort() не останавливает уже улетевший HTTP-запрос. Чтобы более быстрый,
        // но более поздний вызов (например, частое переключение фильтров) не перезаписал
        // состояние устаревшим ответом, ниже сверяем abortRef.current с локальным controller —
        // если он уже заменён более новым вызовом, применённый здесь ответ игнорируется.
        const isStale = () => abortRef.current !== controller

        setState(s => ({ ...s, loading: true }))

        try {
            const response = await fetchRef.current(
                page,
                pageSize,
                controller.signal,
            )

            if (isStale()) return

            const { items, hasMore: more, total } = adapterRef.current({
                response,
                currentItems: stateRef.current.items,
                page,
                pageSize,
            })

            setState(s => ({
                ...s,
                items: [...s.items, ...items],
                page: s.page + 1,
                hasMore: more ?? false,
                total,
                loading: false,
                error: null,
            }))

            // Сбрасываем флаг первоначальной загрузки после успешного выполнения
            initialLoadRef.current = false
        } catch (e: unknown) {
            if (isStale()) return

            if ((e as Error)?.name !== 'AbortError') {
                setState(s => ({ ...s, loading: false, error: e as Error }))
            }
            // Сбрасываем флаг при ошибке тоже
            initialLoadRef.current = false
        }
    }, [pageSize])

    const reset = useCallback(() => {
        abortRef.current?.abort()
        initialLoadRef.current = false
        const freshState: InfiniteScrollState<T> = {
            items: [],
            page: initialPage,
            hasMore: true,
            total: undefined,
            loading: false,
            error: null,
        }
        // loadMore() читает stateRef синхронно сразу после reset() (см. эффект ниже),
        // а setState применяется асинхронно — без этого loadMore ушёл бы со старой page
        stateRef.current = freshState
        setState(freshState)
    }, [initialPage])

    useEffect(() => {
        reset()
        
        if (autoLoad) {
            initialLoadRef.current = true
            loadMore()
        }
    }, [reset, autoLoad, loadMore, ...resetDeps])

    const loaderRef = useIntersection(
        () => {
            // Не загружаем через intersection observer если это первоначальная загрузка
            if (initialLoadRef.current) {
                initialLoadRef.current = false
                return
            }
            loadMore()
        },
        { threshold },
    )

    return {
        ...state,
        loadMore,
        reset,
        loaderRef,
    }
}
